import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Settings, 
  Save, 
  Plus, 
  Trash2, 
  Copy, 
  Target, 
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Strategy Configuration Panel Component
 * Provides interface for creating and managing betting strategies
 */
const StrategyConfigPanel = ({
  onStrategyChange,
  savedStrategies = [],
  currentStrategy = null,
  className = ""
}) => {
  // Local strategy state
  const [localStrategy, setLocalStrategy] = useState({
    id: '',
    name: '',
    description: '',
    unitSystem: 'fixed',
    unitSize: 1,
    rules: [],
    tipSelections: [
      { position: 'T1', enabled: true, weight: 1 },
      { position: 'T2', enabled: false, weight: 1 },
      { position: 'T3', enabled: false, weight: 1 },
      { position: 'T4', enabled: false, weight: 1 },
      { position: 'T5', enabled: false, weight: 1 },
      { position: 'T6', enabled: false, weight: 1 },
      { position: 'T7', enabled: false, weight: 1 },
      { position: 'T8', enabled: false, weight: 1 }
    ]
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [validationErrors, setValidationErrors] = useState([]);
  const [isModified, setIsModified] = useState(false);

  // Update local strategy when currentStrategy prop changes
  useEffect(() => {
    if (currentStrategy) {
      setLocalStrategy(currentStrategy);
      setIsModified(false);
    }
  }, [currentStrategy]);

  // Validation logic
  const validateStrategy = useCallback((strategy) => {
    const errors = [];
    
    if (!strategy.name?.trim()) {
      errors.push('Strategy name is required');
    }
    
    if (strategy.unitSize <= 0) {
      errors.push('Unit size must be greater than 0');
    }
    
    const enabledTips = strategy.tipSelections?.filter(tip => tip.enabled) || [];
    if (enabledTips.length === 0) {
      errors.push('At least one tip selection must be enabled');
    }
    
    return errors;
  }, []);

  // Update validation when strategy changes
  useEffect(() => {
    const errors = validateStrategy(localStrategy);
    setValidationErrors(errors);
  }, [localStrategy, validateStrategy]);

  // Handle strategy field changes
  const handleStrategyChange = useCallback((field, value) => {
    const updatedStrategy = {
      ...localStrategy,
      [field]: value
    };
    
    setLocalStrategy(updatedStrategy);
    setIsModified(true);
    
    // Auto-apply valid strategies
    if (validateStrategy(updatedStrategy).length === 0) {
      onStrategyChange?.(updatedStrategy);
    }
  }, [localStrategy, validateStrategy, onStrategyChange]);

  // Handle tip selection changes
  const handleTipSelectionChange = useCallback((position, field, value) => {
    const updatedTipSelections = localStrategy.tipSelections.map(tip => 
      tip.position === position 
        ? { ...tip, [field]: value }
        : tip
    );
    
    handleStrategyChange('tipSelections', updatedTipSelections);
  }, [localStrategy.tipSelections, handleStrategyChange]);

  // Handle unit system specific changes
  const handleUnitSystemChange = useCallback((unitSystem) => {
    let updatedStrategy = {
      ...localStrategy,
      unitSystem
    };
    
    // Set default unit size based on system
    switch (unitSystem) {
      case 'fixed':
        updatedStrategy.unitSize = 1;
        break;
      case 'perry':
        updatedStrategy.unitSize = 1;
        break;
      case 'custom':
        updatedStrategy.unitSize = 1;
        break;
    }
    
    setLocalStrategy(updatedStrategy);
    setIsModified(true);
    
    if (validateStrategy(updatedStrategy).length === 0) {
      onStrategyChange?.(updatedStrategy);
    }
  }, [localStrategy, validateStrategy, onStrategyChange]);

  // Save strategy
  const handleSaveStrategy = useCallback(() => {
    if (validationErrors.length === 0) {
      const strategyToSave = {
        ...localStrategy,
        id: localStrategy.id || `strategy_${Date.now()}`,
        updatedAt: new Date()
      };
      
      setLocalStrategy(strategyToSave);
      setIsModified(false);
      onStrategyChange?.(strategyToSave);
    }
  }, [localStrategy, validationErrors, onStrategyChange]);

  // Load saved strategy
  const handleLoadStrategy = useCallback((strategy) => {
    setLocalStrategy(strategy);
    setIsModified(false);
    onStrategyChange?.(strategy);
  }, [onStrategyChange]);

  // Create new strategy
  const handleNewStrategy = useCallback(() => {
    const newStrategy = {
      id: `strategy_${Date.now()}`,
      name: 'New Strategy',
      description: '',
      unitSystem: 'fixed',
      unitSize: 1,
      rules: [],
      tipSelections: [
        { position: 'T1', enabled: true, weight: 1 },
        { position: 'T2', enabled: false, weight: 1 },
        { position: 'T3', enabled: false, weight: 1 },
        { position: 'T4', enabled: false, weight: 1 },
        { position: 'T5', enabled: false, weight: 1 },
        { position: 'T6', enabled: false, weight: 1 },
        { position: 'T7', enabled: false, weight: 1 },
        { position: 'T8', enabled: false, weight: 1 }
      ]
    };
    
    setLocalStrategy(newStrategy);
    setIsModified(true);
  }, []);

  // Calculate enabled tips count
  const enabledTipsCount = useMemo(() => {
    return localStrategy.tipSelections?.filter(tip => tip.enabled).length || 0;
  }, [localStrategy.tipSelections]);

  // Get unit system description
  const getUnitSystemDescription = useCallback((system) => {
    switch (system) {
      case 'fixed':
        return 'Bet the same amount on every selection';
      case 'perry':
        return 'Progressive staking system based on the Perry method';
      case 'custom':
        return 'Define custom staking rules and conditions';
      default:
        return '';
    }
  }, []);

  return (
    <Card className={cn("strategy-config-panel", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <span>Strategy Configuration</span>
            {isModified && (
              <Badge variant="outline" className="text-xs">
                Modified
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewStrategy}
              className="h-8 px-2"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSaveStrategy}
              disabled={validationErrors.length > 0 || !isModified}
              className="h-8 px-2"
            >
              <Save className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <Label className="text-sm font-medium">Configuration Issues</Label>
            </div>
            <div className="space-y-1">
              {validationErrors.map((error, index) => (
                <div key={index} className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Saved Strategies Selector */}
        {savedStrategies.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Load Saved Strategy</Label>
            <Select onValueChange={(value) => {
              const strategy = savedStrategies.find(s => s.id === value);
              if (strategy) handleLoadStrategy(strategy.config);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select a saved strategy..." />
              </SelectTrigger>
              <SelectContent>
                {savedStrategies.map((strategy) => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{strategy.name}</span>
                      {strategy.performance && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          ROI: {(strategy.performance.roi * 100).toFixed(1)}%
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator />

        {/* Strategy Configuration Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="tips">Tips</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          {/* Basic Configuration */}
          <TabsContent value="basic" className="space-y-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Strategy Name</Label>
              <Input
                value={localStrategy.name}
                onChange={(e) => handleStrategyChange('name', e.target.value)}
                placeholder="Enter strategy name..."
                className="text-sm"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Description</Label>
              <Textarea
                value={localStrategy.description}
                onChange={(e) => handleStrategyChange('description', e.target.value)}
                placeholder="Describe your strategy..."
                className="text-sm min-h-[80px]"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Unit System</Label>
              <Select value={localStrategy.unitSystem} onValueChange={handleUnitSystemChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Fixed Units</div>
                        <div className="text-xs text-muted-foreground">Same stake every bet</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="perry">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Perry System</div>
                        <div className="text-xs text-muted-foreground">Progressive staking</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Custom Rules</div>
                        <div className="text-xs text-muted-foreground">Define your own rules</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {getUnitSystemDescription(localStrategy.unitSystem)}
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Base Unit Size</Label>
                <Badge variant="outline" className="text-xs">
                  ${localStrategy.unitSize}
                </Badge>
              </div>
              <div className="space-y-3">
                <Slider
                  value={[localStrategy.unitSize]}
                  onValueChange={(value) => handleStrategyChange('unitSize', value[0])}
                  max={100}
                  min={0.1}
                  step={0.1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>$0.10</span>
                  <span>$100.00</span>
                </div>
              </div>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                placeholder="Or enter exact amount..."
                value={localStrategy.unitSize}
                onChange={(e) => handleStrategyChange('unitSize', parseFloat(e.target.value) || 0.1)}
                className="text-sm"
              />
            </div>
          </TabsContent>

          {/* Tip Selection Configuration */}
          <TabsContent value="tips" className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Tip Selections</Label>
              <Badge variant="secondary" className="text-xs">
                {enabledTipsCount} of {localStrategy.tipSelections?.length || 0} enabled
              </Badge>
            </div>

            <div className="space-y-3">
              {localStrategy.tipSelections?.map((tip) => (
                <Card key={tip.position} className={cn(
                  "p-3 transition-colors",
                  tip.enabled ? "bg-primary/5 border-primary/20" : "bg-muted/30"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Target className={cn(
                          "h-4 w-4",
                          tip.enabled ? "text-primary" : "text-muted-foreground"
                        )} />
                        <Label className="font-medium">{tip.position}</Label>
                      </div>
                      <Switch
                        checked={tip.enabled}
                        onCheckedChange={(checked) => 
                          handleTipSelectionChange(tip.position, 'enabled', checked)
                        }
                      />
                    </div>
                    
                    {tip.enabled && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground">Weight:</Label>
                        <Input
                          type="number"
                          min="0.1"
                          max="10"
                          step="0.1"
                          value={tip.weight}
                          onChange={(e) => 
                            handleTipSelectionChange(tip.position, 'weight', parseFloat(e.target.value) || 1)
                          }
                          className="w-16 h-8 text-xs"
                        />
                      </div>
                    )}
                  </div>
                  
                  {tip.enabled && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Stake: ${(localStrategy.unitSize * tip.weight).toFixed(2)} per bet
                    </div>
                  )}
                </Card>
              ))}
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-800">
                  <div className="font-medium mb-1">Tip Selection Guide</div>
                  <div>T1 = Top tip, T2 = Second tip, etc. Weight multiplies your base unit size for each tip.</div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Advanced Configuration */}
          <TabsContent value="advanced" className="space-y-4">
            <div className="text-center py-8 text-muted-foreground">
              <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">Advanced rules and conditions</div>
              <div className="text-xs">Coming in future updates</div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Strategy Summary */}
        {validationErrors.length === 0 && localStrategy.name && (
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-xs text-green-800">
                <div className="font-medium mb-1">Strategy Ready</div>
                <div>
                  {localStrategy.name} • {localStrategy.unitSystem} units • {enabledTipsCount} tips selected
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategyConfigPanel;